import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment } from '@mui/material';

interface AmountInputProps {
  amount: string;
  label: string;
  onAmountChange: (newAmount: string) => void;
  onErrorChange: (hasError: boolean) => void;
  currencySymbol?: string;
  width?: number;
  responsiveWidth?: { [breakpoint: string]: string | number };
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const AmountInput: React.FC<AmountInputProps> = ({ 
  amount, 
  label, 
  onAmountChange, 
  onErrorChange,
  currencySymbol = '$', // Default to $ if not provided
  width = 120, // Default width
  responsiveWidth,
  fullWidth = false,
  size = 'small' // Default to small size to match details field
}) => {
  const [internalAmount, setInternalAmount] = useState(amount);
  const [isAmountValid, setIsAmountValid] = useState(true);

  // Sync internal state with prop changes
  useEffect(() => {
    setInternalAmount(amount);
  }, [amount]);

  // Handle change in amount input
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAmount = event.target.value;
    setInternalAmount(newAmount);

    // Validation to check if the new amount is a valid number
    const parsedAmount = parseFloat(newAmount);
    const valid = !isNaN(parsedAmount);
    setIsAmountValid(valid);
    onErrorChange(!valid);
    onAmountChange(newAmount);
  };

  // Handle formatting to 2 decimal places on blur
  const handleAmountBlur = () => {
    const parsedAmount = parseFloat(internalAmount);
    if (!isNaN(parsedAmount)) {
      const formattedAmount = parsedAmount.toFixed(2);
      setInternalAmount(formattedAmount);
      onAmountChange(formattedAmount);
    }
  };

  return (
    <TextField
      label={label}
      value={internalAmount}
      onChange={handleAmountChange}
      onBlur={handleAmountBlur}
      error={!isAmountValid}
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
      sx={{ width: responsiveWidth ? responsiveWidth : (fullWidth ? '100%' : width) }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {currencySymbol}
          </InputAdornment>
        ),
      }}
    />
  );
};

export default AmountInput; 